import { useState, } from 'react';
import { Link as RouterLink, useParams, useLocation, } from 'react-router-dom';
import { useFirstMountState, } from 'react-use';
import {
    Box,
    Stack,
    Tabs,
    Tab,
    Typography,
    List,
    ListSubheader,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Link,
    Alert,
    Skeleton,
    Tooltip, } from '@mui/material';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuth, useAppbar, useCollection, } from '../../hooks';
import CustomLink from '../custom-link';

const a11yProps = (index) => ({ id: `simple-tab-${index}`, 'aria-controls': `simple-tabpanel-${index}` });

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <Box
            id={`simple-tabpanel-${index}`}
            role='tabpanel'
            aria-labelledby={`simple-tab-${index}`}
            hidden={value !== index}
            sx={{
                py: [2, 2.5, 3],
                px: [1.5, 3, 3.4],
            }}
            { ...other }
        >
            { value === index && <>{children}</> }
        </Box>
    );
};

const LibraryList = ({ title, col, viewAllTo, notCreatedTitleText, Icon }) => {
    const [sortByDate, setSortByDate] = useState('asc');
    const { id, } = useParams();
    const { pathname, } = useLocation();
    const { user, } = useAuth();
    const { documents, isPending, error, } = useCollection({
        col,
        _query: [['uid', '==', user.uid]],
        _orderBy: ['createdAt', sortByDate],
        _limit: 5,
    });
    const { anchorElLibrary, drawerMenuIsOpen, customizedTabsLibraryClose, drawerMenuClose, } = useAppbar();
    const isFirstMount = useFirstMountState();

    const handleToggle = () => {
        setSortByDate((value) => value === 'asc' ? 'desc' : 'asc');
    };

    const handleClose = () => {
        Boolean(anchorElLibrary) && customizedTabsLibraryClose();
        drawerMenuIsOpen && drawerMenuClose();
    };

    return (
        isFirstMount
            ? null
            : isPending
            ? <Box sx={{mt: -2,}}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{mb: -2.25,}}>
                    <Skeleton
                        animation='wave'
                        sx={{
                            height: '62px',
                            width: '128px',
                        }} />
                    <Skeleton
                        animation='wave'
                        sx={{
                            height: '36px',
                            width: '20px',
                        }}
                    />
                </Stack>
                {[1, 2, 3, 4, 5].map((value) => {
                    return (
                        <Skeleton
                                key={value}
                                animation='wave' sx={{
                                    height: ['90px', '104px', '112px'],
                                    mb: [-3.45, -4],
                                    width: '100%',
                            }} />
                    )
                })}
              </Box>
            : error
            ? <Alert severity='error' variant='filled'>{error.message}</Alert>
            : documents && Array.isArray(documents) && documents.length
            ? <List
                        disablePadding
                        aria-labelledby='nested-list-subheader'
                        subheader={
                            <ListSubheader
                                id='nested-list-subheader'
                                component='div'
                                sx={{
                                    color: 'text.primary',
                                    fontWeight: 'normal',
                                    mb: 2,
                                    p: 0,
                                    position: 'static',
                                }}>
                                <Stack
                                    direction='row'
                                    justifyContent='space-between'
                                    alignItems='center'>
                                    <CustomLink
                                        to={viewAllTo}
                                        variant='btn'
                                        onClick={handleClose}
                                        sx={{
                                            border: 1,
                                            borderColor: 'borderColor.main',
                                            color: 'text.primary',
                                            fontSize: 13,
                                            fontWeight: 'normal',
                                            lineHeight: 2.75,
                                            py: 0,
                                            '&:hover': {
                                                border: 1,
                                                borderColor: 'secondary.main',
                                                color: 'secondary.main',
                                            },
                                    }}>
                                        Просмотреть все
                                    </CustomLink>
                                    <Tooltip title='Сортировка по дате создания' placement='top' arrow>
                                        <IconButton onClick={handleToggle} sx={{
                                            p: 0,
                                            '&:hover': {
                                                bgcolor: 'transparent',
                                                color: 'secondary.main',
                                            },
                                        }}>
                                            <CalendarTodayIcon
                                                color={ sortByDate === 'desc' ? 'secondary' : undefined }
                                                sx={{ fontSize: [18, 19, 19, 20], }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </ListSubheader>
                        }>
                        {
                            documents.map((doc, index) => {
                                const isHighlighted = (doc.id === id && pathname === `/${title}/${id}`);

                                return (
                                    <Link
                                        key={doc.id}
                                        component={RouterLink}
                                        to={`/${title}/${doc.id}`}
                                        underline='none'>
                                        <ListItemButton
                                            onClick={handleClose}
                                            sx={{
                                            border: 1,
                                            borderColor: (isHighlighted) ? 'secondary.main' : 'borderColor.main',
                                            mb: documents.length - 1 === index ? 0 : [1, 1.25, 1.5, 1.75],
                                            px: [1.25, 1.5, 1.75, 2],
                                            py: [0.5, 0.75, 1],
                                            '&:hover': {
                                                borderColor: 'secondary.main',
                                                '& .MuiSvgIcon-root': {
                                                    color: 'secondary.main',
                                                },
                                                '& .MuiListItemText-primary': {
                                                    color: 'secondary.main',
                                                }
                                            },
                                        }}>
                                            <ListItemIcon sx={{ minWidth: 'auto', mr: [1.25, 1.5, 1.75, 2], }}>
                                                <Icon sx={{
                                                    color: (isHighlighted) ? 'secondary.main' : 'text.primary',
                                                    fontSize: [21, 23, 25, 27],
                                                }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={doc.title}
                                                secondary={doc.description}
                                                sx={{
                                                    '& .MuiListItemText-primary': {
                                                        color: (isHighlighted) ? 'secondary.main' : 'text.primary',
                                                        display: 'block',
                                                        fontSize: [12, 13, 14],
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    },
                                                    '& .MuiListItemText-secondary': {
                                                        fontSize: [11, 12, 13],
                                                        overflowWrap: 'anywhere',
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </Link>
                                );
                            })
                        }
            </List>
            : <Typography sx={{ fontSize: 14, }}>{notCreatedTitleText}</Typography>
    );
};

export default function ScrollableTabsButtonForce({ sx = [], }) {
    const { user, } = useAuth();
    const { scrollableTabsLibrarySelected, scrollableTabsLibraryChange, } = useAppbar();

    return (
        <Box sx={[
            ...(Array.isArray(sx) ? sx : [sx]),
        ]}>
            <Tabs
                value={scrollableTabsLibrarySelected}
                variant='scrollable'
                allowScrollButtonsMobile
                aria-label='scrollable force tabs'
                indicatorColor='secondary'
                textColor='secondary'
                scrollButtons='auto'
                onChange={scrollableTabsLibraryChange}
                sx={{
                    '& .MuiTab-root': {
                      color: 'text.primary',
                      textTransform: 'none',
                    },
                    '& .MuiTabs-scrollButtons': {
                        color: 'text.primary',
                    },
                }}
            >
                <Tab label='Сеты' {...a11yProps(0)} />
                <Tab label='Папки' {...a11yProps(1)} />
            </Tabs>
                <TabPanel value={scrollableTabsLibrarySelected} index={0}>
                    { user
                        ? <LibraryList
                            title='set'
                            col='sets'
                            viewAllTo='/sets'
                            notCreatedTitleText='Список сетов пуст'
                            Icon={AutoAwesomeMotionIcon}/>
                        : <Typography sx={{fontSize: 14,}}>Авторизуйтесь или зарегистрируйтесь, чтобы начать работать с сетами</Typography>
                    }
                </TabPanel>
                <TabPanel value={scrollableTabsLibrarySelected} index={1}>
                    { user
                        ? <LibraryList
                            title='folder'
                            col='folders'
                            viewAllTo='/folders'
                            notCreatedTitleText='Список папок пуст'
                            Icon={FolderIcon} />
                        : <Typography sx={{fontSize: 14,}}>Авторизуйтесь или зарегистрируйтесь, чтобы начать работать с папками</Typography>
                    }
                </TabPanel>
        </Box>
    );
}